import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Apple } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/privacy", label: "Privacy", testid: "nav-link-privacy" },
  { to: "/terms", label: "Terms", testid: "nav-link-terms" },
  { to: "/support", label: "Support", testid: "nav-link-support" },
];

function Wordmark() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
      <span className="relative block h-9 w-9 overflow-hidden rounded-full ring-1 ring-white/20 transition-shadow duration-300 group-hover:ring-[#1D8A99]/70">
        <img src="/assets/knot-logo.png" alt="Knot Forgotten logo" className="h-full w-full object-cover" />
      </span>
      <span className="leading-none">
        <span className="block text-[15px] font-bold tracking-tight text-white">Knot Forgotten</span>
        <span className="mt-1 hidden text-[10px] font-medium uppercase tracking-[0.22em] text-[#25A5B7] sm:block">
          Boat management
        </span>
      </span>
    </Link>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#0A192F]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Wordmark />

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-white" : "text-slate-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <span
            data-testid="nav-app-store-badge"
            className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-slate-300"
            title="The App Store link will be activated at launch"
          >
            <Apple className="h-3.5 w-3.5" />
            Coming Soon
          </span>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-white/10 bg-[#0A192F]/95 backdrop-blur-xl md:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
